import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogBody,
  Accordion,
  AccordionHeader,
  AccordionBody,
} from "@material-tailwind/react";

import PreviousSSD from '../../sanityInterfaces/PreviousSSD';
// import HeaderBanner from '../ui/HeaderBanner';
import TechnicalProgContent from '../utils/TechnicalProgContent';
import SpeakersContent from '../utils/SpeakersContent';
import WorkshopsContent from '../utils/WorkshopsContent';
import ChairsContent from '../utils/ChairsContent';
import SpecialSessionsContent from '../utils/SpecialSessionsContent';

const PreviousSSDLayout = (props: { versionData: PreviousSSD }) => {
  const [open, setOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [openAccordion, setOpenAccordion] = useState(0);

  const versionData: any = props?.versionData;

  const ignoredKeys = [
    "_id",
    "_type",
    "_createdAt",
    "_updatedAt",
    "_rev",
    "SSDVersionYear",
    "SSDEditionNumber",
    "Date",
    "Place",
    "Description",
    "SSDVersionImage",
    "PHOTOGallery"
  ];

  const hasRealData = (value: any): boolean => {
    if (value === null || value === undefined) return false;

    if (typeof value === "string") {
      return value.trim() !== "";
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return true;
    }

    if (Array.isArray(value)) {
      return value.some((item) => hasRealData(item));
    }

    if (typeof value === "object") {
      return Object.entries(value)
        .filter(([key]) => !ignoredKeys.includes(key))
        .some(([, itemValue]) => hasRealData(itemValue));
    }

    return false;
  };

  const hasSectionData = (sectionKeywords: string[]) => {
    if (!versionData) return false;

    return Object.entries(versionData).some(([key, value]) => {
      if (ignoredKeys.includes(key)) return false;

      const normalizedKey = key.toLowerCase();

      const belongsToSection = sectionKeywords.some((keyword) =>
        normalizedKey.includes(keyword.toLowerCase())
      );

      return belongsToSection && hasRealData(value);
    });
  };

  const hasTechnicalProgramData = hasSectionData([
    "technical",
    "program",
    "plenary",
    "pl",
    "csp",
    "pse",
    "sac",
    "sci",
    "abstract",
    "meeting",
    "closure"
  ]);

  const hasSpeakersData = hasSectionData([
    "speaker",
    "speakers"
  ]);

  const hasWorkshopsData = hasSectionData([
    "workshop",
    "workshops"
  ]);

  const hasChairsData = hasSectionData([
    "chair",
    "chairs"
  ]);

  const hasSpecialSessionsData = hasSectionData([
    "special",
    "session",
    "sessions"
  ]);

  const hasPhotoGalleryData =
    props?.versionData?.PHOTOGallery &&
    props?.versionData?.PHOTOGallery.length > 0 &&
    props?.versionData?.PHOTOGallery.some((item: any) => item?.Title?.asset?._ref);

  const getImageUrl = (ref?: string) => {
    if (!ref) return "";

    return `https://cdn.sanity.io/images/79vgbaej/production/${
      ref.slice(6, -4)
    }.${
      ref.slice(-3)
    }`;
  };

  const handleOpenAccordion = (value: number) => {
    setOpenAccordion(openAccordion === value ? 0 : value);
  };

  const handleIndex = (n: number) => {
    if (!props?.versionData?.PHOTOGallery || props.versionData.PHOTOGallery.length === 0) {
      return;
    }

    if (currentImageIndex + n > props?.versionData?.PHOTOGallery?.length - 1) {
      setCurrentImageIndex(0);
      setCurrentImage(getImageUrl(props?.versionData?.PHOTOGallery[0]?.Title?.asset?._ref));
    } else if (currentImageIndex + n < 0) {
      setCurrentImageIndex(props?.versionData?.PHOTOGallery.length - 1);
      setCurrentImage(
        getImageUrl(
          props?.versionData?.PHOTOGallery[
            props?.versionData?.PHOTOGallery.length - 1
          ]?.Title?.asset?._ref
        )
      );
    } else {
      setCurrentImageIndex(currentImageIndex + n);
      setCurrentImage(
        getImageUrl(
          props?.versionData?.PHOTOGallery[currentImageIndex + n]?.Title?.asset?._ref
        )
      );
    }
  };

  const handleClick = (src: string, index: number) => {
    setCurrentImageIndex(index);
    setCurrentImage(src);
    handleOpen();
  };

  const handleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    handleOpen();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleOpen();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, handleOpen]);

  useEffect(() => {}, [currentImage]);

  const accordionHeaderClass = (isOpen: boolean) =>
    `group border-0 px-0 py-0 hover:text-red-500 transition-colors duration-200 ${
      isOpen ? "text-red-500" : "text-gray-800"
    }`;

  const renderAccordionSection = (
    id: number,
    title: string,
    subtitle: string,
    shouldRender: boolean,
    content: React.ReactNode
  ) => {
    if (!shouldRender) return null;

    const isOpen = openAccordion === id;

    return (
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:border-red-100 hover:shadow-md">
        <Accordion open={isOpen}>
          <AccordionHeader
            onClick={() => handleOpenAccordion(id)}
            className={accordionHeaderClass(isOpen)}
          >
            <div className="flex w-full items-center justify-between gap-4 px-5 py-5 sm:px-6">
              <div className="flex items-center gap-4">
                <span
                  className={`h-11 w-1.5 rounded-full transition-colors duration-300 ${
                    isOpen ? "bg-red-500" : "bg-[#00094f]"
                  }`}
                />

                <div className="text-left">
                  <h2 className="text-lg font-bold text-red-500 sm:text-xl">
                    {title}
                  </h2>
                  <p className="mt-1 text-sm font-normal text-gray-500">
                    {subtitle}
                  </p>
                </div>
              </div>

              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                  isOpen
                    ? "border-red-100 bg-red-50 text-red-500"
                    : "border-gray-200 bg-gray-50 text-[#00094f] group-hover:border-red-100 group-hover:bg-red-50 group-hover:text-red-500"
                }`}
              >
                <svg
                  className={`h-4 w-4 transition-transform duration-300 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </AccordionHeader>

          <AccordionBody className="border-t border-gray-100 px-5 pb-6 pt-5 sm:px-6">
            <div className="rounded-xl bg-gray-50/70 p-4 sm:p-5">
              {content}
            </div>
          </AccordionBody>
        </Accordion>
      </div>
    );
  };

  return (
    <>
      {/* 
        To restore the old HeaderBanner later:
        1. Uncomment the import at the top:
           import HeaderBanner from '../ui/HeaderBanner';
        2. Uncomment the line below.
      */}
      {/* <HeaderBanner title="Previous version" /> */}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="grid gap-8 p-5 sm:p-7 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:p-8">
            <div className="flex flex-col justify-center">
              {props?.versionData?.SSDVersionYear && (
                <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-red-100 bg-red-50 px-4 py-2">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  <span className="text-sm font-bold uppercase tracking-wide text-red-500">
                    Previous SSD Edition
                  </span>
                </div>
              )}

              {props?.versionData?.SSDVersionYear && (
                <h1 className="text-4xl font-extrabold tracking-tight text-[#00094f] sm:text-5xl">
                  {props?.versionData?.SSDVersionYear}
                </h1>
              )}

              {(props?.versionData?.SSDEditionNumber ||
                props?.versionData?.Date ||
                props?.versionData?.Place) && (
                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                  {props?.versionData?.SSDEditionNumber && (
                    <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-4">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                        Edition
                      </p>
                      <p className="mt-2 text-base font-bold leading-6 text-gray-800">
                        {props?.versionData?.SSDEditionNumber} Edition
                      </p>
                    </div>
                  )}

                  {props?.versionData?.Date && (
                    <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-4">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                        Date
                      </p>
                      <p className="mt-2 text-base font-bold leading-6 text-gray-800">
                        {props?.versionData?.Date}
                      </p>
                    </div>
                  )}

                  {props?.versionData?.Place && (
                    <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-4">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                        Location
                      </p>
                      <p className="mt-2 text-base font-bold leading-6 text-gray-800">
                        {props?.versionData?.Place}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {props?.versionData?.Description && (
                <div className="mt-7 rounded-2xl border border-gray-100 border-l-4 border-l-red-500 bg-gray-50/80 p-5 shadow-sm">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-[#00094f]" />
                    <p className="text-sm font-bold uppercase tracking-wide text-[#00094f]">
                      Conference Summary
                    </p>
                  </div>

                  <p className="text-sm leading-7 text-gray-700 sm:text-base">
                    {props?.versionData?.Description}
                  </p>
                </div>
              )}
            </div>

            {props?.versionData?.SSDVersionImage?.asset?._ref && (
              <div className="flex items-center justify-center lg:justify-end">
                <div className="relative w-full max-w-xl">
                  <div className="absolute -left-3 -top-3 h-24 w-24 rounded-2xl bg-red-500/10" />
                  <div className="absolute -bottom-3 -right-3 h-24 w-24 rounded-2xl bg-[#00094f]/10" />

                  <div className="relative overflow-hidden rounded-3xl border border-gray-100 bg-white p-3 shadow-md">
                    <div className="overflow-hidden rounded-2xl bg-gray-100">
                      <img
                        className="aspect-[16/10] w-full object-cover object-center transition-transform duration-500 hover:scale-105"
                        src={getImageUrl(props?.versionData?.SSDVersionImage?.asset?._ref)}
                        alt={`${props?.versionData?.SSDVersionYear || "SSD previous version"} conference location`}
                      />
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3 px-1">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-red-500">
                          Conference Location
                        </p>
                        <p className="mt-1 text-sm font-semibold text-gray-700">
                          {props?.versionData?.Place || "SSD Previous Edition"}
                        </p>
                      </div>

                      {props?.versionData?.Date && (
                        <div className="rounded-full bg-gray-50 px-3 py-2 text-xs font-bold text-[#00094f]">
                          {props?.versionData?.Date}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="my-8">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-[#00094f]">
                Archive Content
              </p>
              <h2 className="mt-1 text-2xl font-extrabold text-gray-900">
                Previous SSD Materials
              </h2>
            </div>

            <div className="hidden h-px flex-1 bg-gradient-to-r from-red-200 via-gray-200 to-transparent sm:block" />
          </div>

          <div className="space-y-4">
            {renderAccordionSection(
              1,
              "Technical Program",
              "Program books, plenaries, tracks, and official technical documents.",
              hasTechnicalProgramData,
              <TechnicalProgContent data={props?.versionData} />
            )}

            {renderAccordionSection(
              2,
              "Speakers",
              "Keynote speakers, invited speakers, and speaker information.",
              hasSpeakersData,
              <SpeakersContent data={props?.versionData} />
            )}

            {renderAccordionSection(
              3,
              "Workshops",
              "Workshop materials, tutorial sessions, and related activities.",
              hasWorkshopsData,
              <WorkshopsContent data={props?.versionData} />
            )}

            {renderAccordionSection(
              4,
              "Chairs",
              "Conference chairs, committee chairs, and organizing roles.",
              hasChairsData,
              <ChairsContent data={props?.versionData} />
            )}

            {renderAccordionSection(
              5,
              "Special Sessions",
              "Special session information and related program content.",
              hasSpecialSessionsData,
              <SpecialSessionsContent data={props?.versionData} />
            )}
          </div>
        </section>

        {hasPhotoGalleryData && (
          <section className="mb-12 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-7">
            <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-[#00094f]">
                  Visual Archive
                </p>
                <h2 className="mt-1 text-2xl font-extrabold text-red-500">
                  Photo Gallery
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {props?.versionData?.PHOTOGallery
                ?.filter((item: any) => item?.Title?.asset?._ref)
                .map((item: any, index: number) => {
                  const imageUrl = getImageUrl(item?.Title?.asset?._ref);

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        handleClick(imageUrl, index);
                      }}
                      className="group overflow-hidden rounded-2xl border border-gray-100 bg-gray-50 p-1 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-red-100 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                      aria-label={`Open gallery item ${index + 1}`}
                    >
                      <img
                        className="aspect-[4/3] w-full rounded-xl object-cover object-center transition duration-300 group-hover:scale-105"
                        src={imageUrl}
                        alt={`SSD gallery item ${index + 1}`}
                      />
                    </button>
                  );
                })}
            </div>
          </section>
        )}
      </main>

      <Dialog
        style={{ background: "transparent" }}
        size="xl"
        className="imgModal"
        open={open}
        handler={handleOpen}
      >
        <DialogBody
          className="p-0 flex items-center justify-center min-h-[500px]"
          onClick={handleBackdropClick}
        >
          <div className="relative flex items-center justify-center w-full h-full">
            <button
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-3 shadow-md backdrop-blur-sm transition duration-300 hover:bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              onClick={(e) => {
                e.stopPropagation();
                handleIndex(-1);
              }}
              aria-label="Previous"
            >
              <img
                width="28"
                height="28"
                src="https://img.icons8.com/ios-filled/50/previous.png"
                alt=""
              />
            </button>

            <div
              className="flex items-center justify-center w-full h-full p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                alt="Selected SSD gallery preview"
                className="max-h-[80vh] max-w-full cursor-default rounded-2xl object-contain shadow-2xl"
                src={currentImage}
              />
            </div>

            <button
              className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-3 shadow-md backdrop-blur-sm transition duration-300 hover:bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              onClick={(e) => {
                e.stopPropagation();
                handleIndex(1);
              }}
              aria-label="Next"
            >
              <img
                width="28"
                height="28"
                src="https://img.icons8.com/ios-filled/50/next.png"
                alt=""
              />
            </button>

            <button
              className="absolute right-4 top-4 z-10 rounded-full bg-white/80 p-3 shadow-md backdrop-blur-sm transition duration-300 hover:bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              onClick={(e) => {
                e.stopPropagation();
                handleOpen();
              }}
              aria-label="Close"
            >
              <img
                width="18"
                height="18"
                src="https://img.icons8.com/ios-filled/50/delete-sign--v1.png"
                alt=""
              />
            </button>
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
};

export default PreviousSSDLayout;